import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Divider,
  Link,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Class, ComputedQuizScore, DbUser, UserCtx } from "../../../interfaces";
import { WrapperCenterMessage } from "../../v2/learning/WrapperCenterMessage";
import { ReactComponent as IconCheck } from "../../../assets/IconCheck.svg";
import { ReactComponent as ArrowIcon } from "../../../assets/ArrowRightWhiteIcon.svg";
import { Module, Target, Question, Course } from "../../utils/CourseTree";
import {
  CourseMetadata,
  LtiOptionActionParams,
  UserQuizSubmission,
} from "@backend-types";
import { ModuleProgressIndicator } from "../../v2/utils/ModuleProgressIndicator";
import { ButtonZoom } from "../../v2/utils/ButtonZoom";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import ModuleCompletedAnimation from "../../../assets/ModuleCompletedAnimation.json";
import { Role, _AuthType } from "../../../enums";
import { StatusCard } from "./StatusCard";
import { RateDigit } from "./RateDigit";
import { QuizScoreItem } from "./QuizScoreItem";
import { StatusChipProps } from "./StatusChip";
import { generateGtmEvent } from "../../../Helpers";
import { useHistory } from "react-router-dom";
import { UserContext } from "../../../context/UserContext";

const useStyles = makeStyles((theme) => ({
  root: {
    width: 876,
    position: "relative",
  },
  rootv2: {
    maxWidth: "1136px",
    position: "relative",
  },
  button: {
    width: 195,
    padding: "0px",
  },
  statistic: {
    color: "#747474",
  },
  statisticVal: {
    color: "#747474",
    fontWeight: theme.typography.fontWeightBold,
  },
  animationContainer: {
    // display: "flex",
    // justifyContent: "center",
    // alignItems: "center",
  },
  moduleCompletedAnimation: {
    width: 264,
    height: 264,
  },
  arrow: {
    marginLeft: 8,
    width: 17,
    height: 19,
  },
  card: {
    marginTop: 35,
    backgroundColor: "transparent",
    border: "0px",
  },
  cardHeader: {
    letterSpacing: "1.6px",
    fontWeight: theme.typography.fontWeightMedium,
    color: "#747474",
  },
  cardModuleTitle: {
    letterSpacing: "-0.12px",
  },
  iconCheckCircle: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    backgroundColor: theme.palette.success.main,
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  percentageStatus: {
    fontWeight: 600,
    fontSize: "1.25em",
    color: "#787676",
    letterSpacing: "0.15px",
  },
  percentageStatusV2: {
    marginRight: "auto",
    marginLeft: "24px",
    fontWeight: 600,
    fontSize: "1.25em",
    color: theme.palette.neutral.dark,
    letterSpacing: "0.15px",
    fontFamily: theme.typography.fontFamily,
  },
  showMoreButton: {
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: 700,
    height: "48px",
    padding: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
  },
  barButton: {
    height: "52px",
    borderRadius: "12px",
    padding: "0px 16px",
  },
  cardRoot: {
    borderRadius: "8px !important",
    "&:active": {
      backgroundColor: "#F2F2F2",
      borderColor: "#0092E0",
    },
  },
  accordionRoot: {
    padding: "0px",
    backgroundColor: "transparent",
    overflow: "hidden",
    boxSizing: "border-box",
    margin: "0px !important",
    borderRadius: "8px",

    "& .MuiAccordionSummary-root": {
      minHeight: "unset !important",
    },
  },
  accordionSummary: {
    margin: "0px !important",
    padding: "0px",
    paddingLeft: "16px",
    paddingRight: "16px",
    borderRadius: "8px",
    height: "58px",

    "&:hover": {
      backgroundColor: "#F2F2F2",
    },
    "&:active": {
      backgroundColor: "#F2F2F2",
    },
    "& .MuiAccordionSummary-content": {
      margin: "0px !important",
    },
  },
  accordionDetails: {
    marginBottom: "24px",
  },
  accordionHeading: {
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "26px",
    color: theme.palette.text.primary,
    [theme.breakpoints.down("md")]: {
      fontSize: "16px",
      lineHeight: "26px",
    },
  },
  expandChevron: {
    fontSize: "20px",
    color: theme.palette.text.primary,
    [theme.breakpoints.down("md")]: {
      fontSize: "16px",
      lineHeight: "26px",
    },
  },
}));

interface Lti1p3EndModuleProps {
  currentModule: Module;
  currentTarget: Target;
  currentQuestion: Question;
  moduleQuizSubmissions: UserQuizSubmission[];
  nextQuestion: Question | null | undefined;
  nextTarget: Target | null | undefined;
  nextModule: Module | null | undefined;
  currentClass?: Class;
  currentUser: DbUser;
  currentCourse: Course | null;
  courseMetadata?: CourseMetadata;
  handleGoToProblem: (
    goToProblem: Question | null,
    suppressEoT?: boolean,
    suppressEoM?: boolean,
    options?: LtiOptionActionParams
  ) => void;
  handleMoveForward: () => void;
  handleModuleHome: () => void;
  handleBackToCanvas: () => void;
}

export const Lti1p3EndModule = (props: Lti1p3EndModuleProps) => {
  const {
    currentModule,
    currentTarget,
    nextQuestion,
    nextModule,
    nextTarget,
    currentUser,
    currentClass,
    currentCourse,
    courseMetadata,
    handleMoveForward,
    handleModuleHome,
    handleBackToCanvas,
  } = props;
  const userCtx = useContext(UserContext) as UserCtx;
  const classes = useStyles();
  const history = useHistory();
  const useV2ModuleEnd = courseMetadata?.learning_experience?.use_v2_module_end;
  const useMinimalModuleCompletion =
    courseMetadata?.learning_experience?.use_minimal_module_completion;
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const { authType, ltiReturnUrl } = userCtx.authHook;

  const closeTabMessage =
    authType === _AuthType.LTI &&
    currentUser.role === Role.student &&
    (courseMetadata?.deeplink_asset_type === "module" ||
      courseMetadata?.deeplink_asset_type === "course") &&
    (!nextTarget || nextTarget.is_asset_locked);
  const deeplinkTargetMode =
    courseMetadata?.deeplink_asset_type === "target" &&
    !courseMetadata.deeplink_asset_unlocked &&
    !courseMetadata.use_digit_study_tool;
  const showBackToCanvas = authType === _AuthType.LTI && ltiReturnUrl;

  // if deeplinked at the target level, auto expand it and hide the expand button
  const [scoreListExpanded, setScoreListExpanded] = useState<boolean>(deeplinkTargetMode);

  const targetGroups = currentModule.target_groups;
  const currentModuleName = currentModule.getFormattedModuleName();

  const quizScore = useMemo<ComputedQuizScore>(() => {
    const targets = currentModule?.children as Target[];
    return userCtx.calculateQuizScore(targets);
  }, [currentModule, userCtx]);

  // Map of target IDs to their respective primary path points and max_points
  const scoreMap = useMemo<Map<string, ComputedQuizScore>>(() => {
    const targets = currentModule.getAllTargets() as Target[];
    const pMap = new Map<string, ComputedQuizScore>();

    targets.forEach((target) => {
      const score = userCtx.calculateQuizScore([target]);
      pMap.set(target.id, score);
    });

    return pMap;
  }, [currentModule, userCtx]);

  const showFeedback = useMemo(() => {
    return quizScore.invalid !== "No Quiz";
  }, [quizScore.invalid]);

  const sortedScoreList = useMemo(() => {
    const targets = currentModule.getAllTargets() as Target[];
    const scoreList = Array.from(scoreMap.entries()).map(
      ([targetId, { points, max_points, invalid, display_as }]) => {
        const target = targets.find((item) => item.id === targetId);
        const question = target?.getFirstQuestion();
        const percentage = max_points > 0 ? points / max_points : 0;
        return {
          question: question,
          targetName: target ? target.getFormattedTargetName() : "Unknown",
          percentage: percentage,
          points,
          max_points,
          invalid,
          display_as,
        };
      }
    );
    scoreList.sort((a, b) => {
      if (a.max_points > 0 && b.max_points > 0) {
        return a.percentage - b.percentage;
      } else if (a.max_points === 0 && b.max_points === 0) {
        return 0;
      } else if (a.max_points > 0) {
        return -1;
      } else {
        return 1;
      }
    });
    return scoreList;
  }, [scoreMap, currentModule]);

  const numberOfTargetsBelowThreshold = useMemo(() => {
    const count = Array.from(scoreMap.values()).filter(
      ({ points, max_points, invalid }) => {
        const percentage = max_points > 0 ? points / max_points : 1;
        return percentage < 0.8 && !invalid;
      }
    ).length;
    return count;
  }, [scoreMap]);

  const trimmedScoreList = useMemo(() => {
    return sortedScoreList.slice(0, !scoreListExpanded ? 3 : sortedScoreList.length);
  }, [sortedScoreList, scoreListExpanded]);

  useEffect(() => {
    if (lottieRef?.current) {
      lottieRef?.current.play();
    }
  }, []);

  const handleBackToHome = () => {
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: "review completed modules",
      text: "digit home",
    });
    history.push({
      pathname: "/learning-overview",
    });
  };

  let cardStatus: StatusChipProps = {
    text: "Impressive",
    color: "success",
  };

  if (numberOfTargetsBelowThreshold > 0) {
    cardStatus = {
      text: "Almost there!",
      color: "warning",
    };
  }

  const showRating = useV2ModuleEnd || !currentCourse?.higher_ed;

  const isExit = !nextQuestion;

  // ridiculous design requirements wanting dynamic height based alignments
  const leftBoxRef = useRef<Element | null>(null);
  const rightBoxRef = useRef<Element | null>(null);
  const [leftHeight, setLeftHeight] = useState(0);
  const [rightHeight, setRightHeight] = useState(0);

  useEffect(() => {
    const ref = leftBoxRef.current;
    const observer = new ResizeObserver((entries) => {
      setLeftHeight(entries[0].contentRect.height);
    });
    ref && observer.observe(ref);
    return () => {
      ref && observer.unobserve(ref);
    };
  }, []);

  useEffect(() => {
    const ref = rightBoxRef.current;
    const observer = new ResizeObserver((entries) => {
      setRightHeight(entries[0].contentRect.height);
    });
    ref && observer.observe(ref);
    return () => {
      ref && observer.unobserve(ref);
    };
  }, []);

  const leftAlignment = useMemo(() => {
    if (rightHeight > leftHeight) {
      return "start";
    } else {
      return "end";
    }
  }, [rightHeight, leftHeight]);

  const condensedResults = useMemo(() => {
    const targets = currentModule.getAllTargets() as Target[];
    return targetGroups.length > 0 || targets.length < 4;
  }, [currentModule, targetGroups]);

  const titleText = useMinimalModuleCompletion
    ? "See completed modules"
    : "Review completed modules";

  const bodyText = useMinimalModuleCompletion
    ? "You can see completed modules by visiting the Digit home page."
    : "You can review completed modules by visiting the Digit home page.";

  return !useV2ModuleEnd ? (
    <WrapperCenterMessage>
      <Box className={classes.root}>
        <Stack direction="row" justifyContent="center">
          <Box sx={{ padding: "0px 21px", width: "592px" }}>
            {!useMinimalModuleCompletion && (
              <>
                <Typography variant="h1" sx={{ fontSize: "3em", marginBottom: "24px" }}>
                  Way to go, {currentUser.first_name}!
                </Typography>
                <Typography variant="body1" sx={{ color: "#484848" }}>
                  {currentModule?.summary}
                </Typography>
              </>
            )}
            {/* Module card */}
            <Box
              className={classes.card}
              sx={useMinimalModuleCompletion ? { width: 592 } : { width: 421 }}>
              <Stack direction="row" alignItems="center" sx={{ marginBottom: "16px" }}>
                <Box className={classes.iconCheckCircle}>
                  <IconCheck
                    style={{
                      width: "16px",
                      height: "16px",
                    }}
                  />
                </Box>
                <Typography variant="body2" className={classes.cardHeader}>
                  {courseMetadata?.language_overrides?.module?.singular?.toUpperCase() ??
                    "MODULE"}{" "}
                  COMPLETE
                </Typography>
              </Stack>
              <Typography variant="h4" className={classes.cardModuleTitle}>
                {currentModuleName}
              </Typography>
              {!useMinimalModuleCompletion && (
                <>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ marginTop: "24px" }}>
                    <ModuleProgressIndicator
                      moduleId={currentModule.id}
                      percentage={currentModule.progress}
                      moduleName={currentModuleName}
                      style={classes.percentageStatus}
                      progressBarSx={{
                        position: "relative",
                        width: "259px",
                        display: "inline-block",
                      }}
                    />
                  </Stack>
                </>
              )}

              {useMinimalModuleCompletion && (
                <>
                  <Typography
                    variant="h4"
                    className={classes.cardModuleTitle}
                    sx={{ marginTop: "28px" }}>
                    {titleText}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      lineHeight: "24px",
                      marginTop: "8px",
                    }}>
                    {bodyText}
                  </Typography>
                </>
              )}
              {!closeTabMessage && (
                <Stack
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                  sx={{ marginTop: "24px" }}>
                  <ButtonZoom
                    className={classes.button}
                    size="large"
                    variant="outlined"
                    color="primary"
                    onClick={handleModuleHome}>
                    {courseMetadata?.language_overrides?.module?.singular ?? "Module"}{" "}
                    home
                  </ButtonZoom>
                  <ButtonZoom
                    sx={{ marginLeft: "24px" }}
                    className={classes.button}
                    size="large"
                    variant="contained"
                    color="primary"
                    onClick={handleMoveForward}>
                    {isExit ? "Exit" : "Move forward"}
                    <SvgIcon className={classes.arrow}>
                      <ArrowIcon />
                    </SvgIcon>
                  </ButtonZoom>
                </Stack>
              )}
            </Box>

            {courseMetadata?.learning_experience?.feedback_survey_link && (
              <Box
                sx={{
                  maxWidth: "484px",
                  marginTop: "24px",
                  borderRadius: "16px",
                  backgroundColor: "#D6F0FA",
                  padding: "24px 32px",
                  transition: "box-shadow 500ms ease-in-out",
                  "&:hover": {
                    boxShadow: "0px 3px 10px 0px rgba(0, 0, 0, 0.12)",
                  },
                }}>
                <Typography
                  variant="h6"
                  lineHeight="24px"
                  color="#191919"
                  fontWeight={500}>
                  Your opinion matters! Please give us your&nbsp;
                  <Link
                    href={courseMetadata?.learning_experience?.feedback_survey_link}
                    target="_blank"
                    color="#191919">
                    feedback
                  </Link>
                  .
                </Typography>
              </Box>
            )}

            {showRating && (
              <Box sx={{ marginTop: "48px" }}>
                <RateDigit
                  key={`rate-digit-${currentUser.id}-${currentTarget.id}`}
                  currentUser={currentUser}
                  assetId={currentTarget.id}
                  assetType={"Module"}
                  currentClass={currentClass}
                  currentCourse={currentCourse}
                  courseMetadata={courseMetadata}
                />
              </Box>
            )}
          </Box>
          {!useMinimalModuleCompletion && (
            <>
              <Box className={classes.animationContainer}>
                <Lottie
                  lottieRef={lottieRef}
                  className={classes.moduleCompletedAnimation}
                  animationData={ModuleCompletedAnimation}
                  loop={false}
                  autoplay={false}
                />
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </WrapperCenterMessage>
  ) : (
    <WrapperCenterMessage
      buttons={
        !closeTabMessage ? (
          <>
            <ButtonZoom
              className={classes.barButton}
              size="large"
              variant="outlined"
              color="primary"
              onClick={handleModuleHome}>
              {courseMetadata?.language_overrides?.module?.singular ?? "Module"} home
            </ButtonZoom>
            <ButtonZoom
              sx={{ marginLeft: "24px" }}
              className={classes.barButton}
              size="large"
              variant="contained"
              color="primary"
              onClick={
                deeplinkTargetMode && showBackToCanvas
                  ? () => {
                      generateGtmEvent({
                        event: "link",
                        action: "click",
                        name: "onclick",
                        type: "internal link",
                        region: "main content",
                        section: "what to do next",
                        text: "back to canvas",
                      });
                      handleBackToCanvas();
                    }
                  : handleMoveForward
              }>
              {!nextModule && !nextTarget
                ? deeplinkTargetMode
                  ? "Back to Canvas"
                  : "Exit"
                : "Move forward"}
              <SvgIcon className={classes.arrow}>
                <ArrowIcon />
              </SvgIcon>
            </ButtonZoom>
          </>
        ) : undefined
      }>
      <Box className={classes.rootv2}>
        <Stack direction="row" gap="32px" width="100%" minWidth="0px">
          <Box
            minWidth="552px"
            alignSelf={leftAlignment}
            // marginTop="auto"
            ref={leftBoxRef}>
            {!useMinimalModuleCompletion && (
              <Typography
                variant="h1"
                fontSize="40px"
                lineHeight="52px"
                sx={{ marginBottom: "24px" }}>
                Keep going, {currentUser.first_name}!
              </Typography>
            )}

            <Stack direction="column" sx={{ position: "relative" }}>
              <Stack direction="row" alignItems="center" sx={{ marginBottom: "16px" }}>
                <IconCheck
                  style={{
                    width: "16px",
                    height: "16px",
                    color: "#48A84A",
                    marginRight: "8px",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ letterSpacing: "1.6px" }}
                  textTransform="uppercase">
                  {deeplinkTargetMode
                    ? "Lesson"
                    : courseMetadata?.language_overrides?.module?.singular ??
                      "Module"}{" "}
                  Completed
                </Typography>
              </Stack>
              <Typography variant="h4" lineHeight="24px" fontSize="24px">
                {deeplinkTargetMode
                  ? `Module ${currentModule.order}.${
                      currentTarget.order
                    } - ${currentModuleName.replace(/^Module \d+\s*[:-]\s*/, "")}`
                  : currentModuleName}
              </Typography>
            </Stack>
            {!useMinimalModuleCompletion && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ marginTop: "24px" }}>
                <ModuleProgressIndicator
                  moduleId={currentModule.id}
                  percentage={currentModule.progress}
                  moduleName={currentModuleName}
                  style={classes.percentageStatusV2}
                  progressBarSx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: "330px",
                    display: "inline-block",
                  }}
                />
              </Stack>
            )}
            {!deeplinkTargetMode && (
              <>
                {showBackToCanvas && !nextModule && !nextTarget && (
                  <>
                    <Typography
                      variant="h4"
                      className={classes.cardModuleTitle}
                      sx={{ marginTop: "24px" }}>
                      Go to module conclusion
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, lineHeight: "24px", marginTop: "8px" }}>
                      Return to Canvas to finish the module and prep for the exam.
                    </Typography>

                    <ButtonZoom
                      type="button"
                      sx={{ marginTop: "16px", height: "56px" }}
                      size="large"
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        generateGtmEvent({
                          event: "link",
                          action: "click",
                          name: "onclick",
                          type: "internal link",
                          region: "main content",
                          section: "go to module conclusion",
                          text: "back to canvas",
                        });
                        handleBackToCanvas();
                      }}>
                      Back to Canvas
                    </ButtonZoom>
                  </>
                )}
                <Typography
                  variant="h4"
                  className={classes.cardModuleTitle}
                  sx={{ marginTop: "28px" }}>
                  {titleText}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, lineHeight: "24px", marginTop: "8px" }}>
                  {bodyText}
                </Typography>
                <ButtonZoom
                  type="button"
                  sx={{ marginTop: "16px", height: "56px" }}
                  size="large"
                  variant="outlined"
                  color="primary"
                  onClick={handleBackToHome}>
                  Digit home
                </ButtonZoom>
              </>
            )}
            {showRating && (
              <Box sx={{ marginTop: targetGroups.length > 0 ? "48px" : "24px" }}>
                <RateDigit
                  key={`rate-digit-${currentUser.id}-${currentTarget.id}`}
                  currentUser={currentUser}
                  assetId={currentTarget.id}
                  assetType={"Module"}
                  currentClass={currentClass}
                  currentCourse={currentCourse}
                  courseMetadata={courseMetadata}
                />
              </Box>
            )}
          </Box>
          {!useMinimalModuleCompletion && (
            <Box
              marginTop="auto"
              ref={rightBoxRef}
              flexGrow={1}
              sx={{ position: "relative", minWidth: "552px" }}>
              <StatusCard fillVertical status={cardStatus} gradedQuizScores={quizScore}>
                <Stack direction="column" height="100%">
                  <Typography variant="h4" sx={{ marginBottom: "8px", marginTop: "8px" }}>
                    {numberOfTargetsBelowThreshold > 0
                      ? "Try additional learning"
                      : "All-star performer"}
                  </Typography>
                  {!deeplinkTargetMode && showFeedback && (
                    <Typography
                      variant="h4"
                      sx={{ marginBottom: "32px" }}
                      fontSize="16px"
                      lineHeight="26px">
                      {numberOfTargetsBelowThreshold === 0
                        ? `You scored at least 80% in all lessons`
                        : `You scored less than 80% in ${numberOfTargetsBelowThreshold} 
                  ${numberOfTargetsBelowThreshold === 1 ? "lesson" : "lessons"}`}
                    </Typography>
                  )}
                  {showFeedback ? (
                    <>
                      {deeplinkTargetMode ? (
                        <Typography
                          variant="body2"
                          sx={{ marginBottom: "32px" }}
                          fontSize="14px"
                          lineHeight="24px">
                          {numberOfTargetsBelowThreshold > 0
                            ? `Go back and review additional
                  learning because overall students who do the additional learning score
                  better on the exams.`
                            : `We recommend reviewing the additional learning to keep up the excellent work. Overall students who do the additional learning score better on the exams.`}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ marginBottom: "32px" }}
                          fontSize="14px"
                          lineHeight="24px">
                          {numberOfTargetsBelowThreshold > 0
                            ? `You scored less than 80% on ${numberOfTargetsBelowThreshold} ${
                                numberOfTargetsBelowThreshold === 1 ? "lesson" : "lessons"
                              }. You can review as many times as
                  needed without lowering your score. Go back and review additional
                  learning because overall students who do the additional learning score
                  better on the exams.`
                            : `You scored at least 80% in all lessons. That’s really good! You can review as many times as needed without lowering your score. We recommend reviewing the additional learning to keep up the excellent work. Overall students who do the additional learning score better on the exams.`}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Box sx={{ marginTop: "24px" }}></Box>
                  )}

                  <Stack
                    direction="column"
                    gap={targetGroups.length > 0 ? "0px" : "16px"}
                    sx={{
                      marginBottom: targetGroups.length > 0 ? "8px" : "32px",
                      transition: "height 0.2s ease-in-out",
                      overflow: "hidden",
                      height: condensedResults
                        ? "auto"
                        : scoreListExpanded
                        ? `${
                            58 * trimmedScoreList.length +
                            16 * (trimmedScoreList.length - 1)
                          }px`
                        : "208px",
                    }}>
                    {targetGroups.length > 0
                      ? targetGroups.map((targetGroup, index) => {
                          return (
                            <Card
                              className={classes.cardRoot}
                              sx={{
                                mb: index === targetGroups.length - 1 ? 0 : "24px", // Remove margin for the last item
                              }}>
                              <Accordion
                                key={targetGroup.id}
                                className={classes.accordionRoot}>
                                <AccordionSummary
                                  className={classes.accordionSummary}
                                  expandIcon={
                                    <i
                                      className={`${classes.expandChevron} fa-solid fa-chevron-down`}></i>
                                  }>
                                  <Typography
                                    variant="h4"
                                    component="div"
                                    className={classes.accordionHeading}>
                                    {targetGroup.name}
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                  className={classes.accordionDetails}
                                  sx={{ padding: "0px !important" }}>
                                  <Stack
                                    direction="column"
                                    sx={{
                                      marginTop: "0px",
                                      transition: "height 0.2s ease-in-out",
                                      overflow: "hidden",
                                    }}>
                                    {targetGroup.target_group_members.map(
                                      (targetGroupMember, index) => {
                                        const scoreItem = sortedScoreList.find(
                                          (listItem) =>
                                            listItem.question?.target_id ===
                                            targetGroupMember.target_id
                                        );

                                        return (
                                          <>
                                            <Stack sx={{ marginX: "24px" }}>
                                              <QuizScoreItem
                                                onClick={() => {
                                                  props.handleGoToProblem(
                                                    scoreItem?.question ?? null,
                                                    true,
                                                    true,
                                                    { retry: true }
                                                  );
                                                  generateGtmEvent({
                                                    event: "link",
                                                    action: "click",
                                                    name: "onclick",
                                                    type: "internal link",
                                                    region: "main content",
                                                    section:
                                                      numberOfTargetsBelowThreshold > 0
                                                        ? "Try additional learning"
                                                        : "All-star performer",
                                                    text: scoreItem?.targetName ?? "",
                                                    component: `Keep going, ${currentUser.first_name}!`,
                                                  });
                                                }}
                                                text={scoreItem?.targetName ?? ""}
                                                score={scoreItem}
                                                scoreDisplay={quizScore.display_as}
                                                groupTargets={true}
                                                quizStatus={quizScore.invalid}
                                              />
                                              {index !==
                                                targetGroup.target_group_members.length -
                                                  1 && (
                                                <Divider sx={{ borderWidth: "1px" }} />
                                              )}
                                            </Stack>
                                          </>
                                        );
                                      }
                                    )}
                                  </Stack>
                                </AccordionDetails>
                              </Accordion>
                            </Card>
                          );
                        })
                      : trimmedScoreList.map((scoreItem) => {
                          return (
                            <QuizScoreItem
                              onClick={() => {
                                props.handleGoToProblem(
                                  scoreItem.question ?? null,
                                  true,
                                  true,
                                  { retry: true }
                                );
                                generateGtmEvent({
                                  event: "link",
                                  action: "click",
                                  name: "onclick",
                                  type: "internal link",
                                  region: "main content",
                                  section:
                                    numberOfTargetsBelowThreshold > 0
                                      ? "Try additional learning"
                                      : "All-star performer",
                                  text: scoreItem.targetName,
                                  component: `Keep going, ${currentUser.first_name}!`,
                                });
                              }}
                              text={scoreItem.targetName}
                              score={scoreItem}
                              scoreDisplay={scoreItem.display_as}
                              quizStatus={scoreItem.invalid}
                            />
                          );
                        })}
                  </Stack>
                  {!condensedResults && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ marginTop: "auto", marginBottom: "8px" }}>
                      <Button
                        className={classes.showMoreButton}
                        onClick={() => {
                          setScoreListExpanded(!scoreListExpanded);
                        }}>
                        <i
                          className={`fa-regular ${
                            scoreListExpanded ? "fa-chevron-up" : "fa-chevron-down"
                          }`}></i>
                        <Typography
                          variant="h4"
                          component="span"
                          sx={{ marginLeft: "8px" }}
                          fontSize="inherit"
                          fontWeight="inherit"
                          color="inherit">
                          {scoreListExpanded ? "Show less" : "Show more"}
                        </Typography>
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </StatusCard>
            </Box>
          )}
        </Stack>
      </Box>
    </WrapperCenterMessage>
  );
};
